import { Controller } from "@hotwired/stimulus"

const DESKTOP_MEDIA_QUERY = "(min-width: 768px)"

// Drives the app sidebar in both of its modes. Below the desktop breakpoint the
// topbar button opens the slide-in drawer, which is what every layout gets. At
// desktop width the same button collapses the rail instead, but only in layouts
// that opt in by supplying a URL to save the choice to — the drawer-only
// layouts are left exactly as they were.
export default class extends Controller {
  static targets = ["sidebar", "overlay", "shell", "toggleButton"]
  static values = { collapseUrl: String }

  disconnect() {
    this.close()
  }

  toggle() {
    if (this.collapsible) {
      this.toggleCollapsed()
      return
    }

    // The drawer is a phone thing. At desktop width a layout whose burger is still
    // on screen (Workplace, where it drives the app switcher instead — TMS-1266)
    // must not leave a stray is-open behind that surfaces once the window shrinks.
    if (window.matchMedia(DESKTOP_MEDIA_QUERY).matches) return
    if (!this.hasSidebarTarget) return

    if (this.sidebarTarget.classList.contains("is-open")) {
      this.close()
    } else {
      this.open()
    }
  }

  open() {
    if (!this.hasSidebarTarget) return

    this.sidebarTarget.classList.add("is-open")
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.add("is-visible")
    }
    document.body.classList.add("sidebar-open")
  }

  close() {
    if (this.hasSidebarTarget) {
      this.sidebarTarget.classList.remove("is-open")
    }
    if (this.hasOverlayTarget) {
      this.overlayTarget.classList.remove("is-visible")
    }
    document.body.classList.remove("sidebar-open")
  }

  closeOnEscape() {
    if (this.hasSidebarTarget && this.sidebarTarget.classList.contains("is-open")) {
      this.close()
    }
  }

  get collapsible() {
    return this.hasShellTarget &&
      this.hasCollapseUrlValue &&
      window.matchMedia(DESKTOP_MEDIA_QUERY).matches
  }

  toggleCollapsed() {
    const collapsed = this.shellTarget.classList.toggle("is-nav-collapsed")

    if (this.hasToggleButtonTarget) {
      this.toggleButtonTarget.setAttribute("aria-expanded", String(!collapsed))
    }

    this.saveCollapsed(collapsed)
  }

  // Fire and forget: the rail has already collapsed on screen, and a failed save
  // only costs the student the preference on their next visit.
  saveCollapsed(collapsed) {
    fetch(this.collapseUrlValue, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json",
        "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""
      },
      credentials: "same-origin",
      body: JSON.stringify({ preferences: { nav_collapsed: collapsed } })
    }).catch(() => {})
  }
}
